<template>
  <div class="api-management">
    <div class="page-header">
      <h2>自动化测试接口管理</h2>
      <div class="header-actions">
        <el-button type="warning" icon="el-icon-document" @click="goToReport">测试报告</el-button>
        <el-button type="success" icon="el-icon-download" @click="handleExport">导出</el-button>
        <el-button type="info" icon="el-icon-upload2" @click="showImportDialog">导入</el-button>
        <el-button type="primary" icon="el-icon-plus" @click="showCreateDialog">新增接口</el-button>
      </div>
    </div>

    <!-- 搜索表单 -->
    <el-form :inline="true" :model="searchForm" class="search-form">
      <el-form-item label="接口名称">
        <el-input v-model="searchForm.api_name" placeholder="请输入接口名称" clearable />
      </el-form-item>
      <el-form-item label="接口类型">
        <el-select v-model="searchForm.api_type" placeholder="请选择" clearable>
          <el-option label="登录接口" value="login" />
          <el-option label="普通接口" value="other" />
        </el-select>
      </el-form-item>
      <el-form-item label="主模块">
        <el-select v-model="searchForm.module" placeholder="请选择" clearable filterable>
          <el-option
            v-for="item in moduleList"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="子模块">
        <el-select v-model="searchForm.sub_module" placeholder="请选择" clearable filterable allow-create>
          <el-option
            v-for="item in subModuleList"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="标签">
        <el-select v-model="searchForm.tag" placeholder="请选择" clearable filterable allow-create default-first-option>
          <el-option
            v-for="item in tagList"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="searchForm.status" placeholder="请选择" clearable>
          <el-option label="启用" value="enabled" />
          <el-option label="禁用" value="disabled" />
        </el-select>
      </el-form-item>
      <el-form-item label="测试类型">
        <el-select v-model="searchForm.test_type" placeholder="请选择" clearable>
          <el-option label="独立型" value="independent" />
          <el-option label="依赖型" value="need_data" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleSearch">搜索</el-button>
        <el-button @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 数据表格 - 树形结构 -->
    <el-table
      v-loading="loading"
      :data="tableData"
      border
      style="width: 100%"
      row-key="id"
      :row-class-name="getRowClassName"
      :tree-props="{children: 'children', hasChildren: 'hasChildren'}"
      :default-expand-all="false"
      :lazy="true"
      :load="loadChildren">

      <el-table-column prop="api_name" label="接口名称" min-width="250" fixed="left">
        <template slot-scope="scope">
          <div :class="['api-name-cell', {
            'is-login': scope.row.is_login,
            'is-module': !scope.row.is_leaf && !scope.row.sub_module,
            'is-sub-module': !scope.row.is_leaf && scope.row.sub_module
          }]">
            <i v-if="scope.row.is_login" class="el-icon-key key-icon"></i>
            <i v-else-if="!scope.row.is_leaf && scope.row.sub_module" class="el-icon-folder-opened folder-icon"></i>
            <i v-else-if="!scope.row.is_leaf" class="el-icon-menu module-icon"></i>
            <span>{{ scope.row.api_name }}</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="api_type" label="类型" width="100" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.api_type === 'login'" type="warning" size="small">登录</el-tag>
          <el-tag v-else-if="scope.row.api_type" type="primary" size="small">普通</el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="module" label="主模块" width="150" align="center">
        <template slot-scope="scope">
          <span v-if="scope.row.module && scope.row.is_leaf" class="module-text">
            {{ scope.row.module }}
          </span>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="sub_module" label="子模块" width="150" align="center">
        <template slot-scope="scope">
          <span v-if="scope.row.sub_module" class="sub-module-text">
            {{ scope.row.sub_module }}
          </span>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="is_main" label="是否主模块" width="80" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.is_leaf" :type="scope.row.is_main === 'yes' ? 'success' : 'info'" size="small">
            {{ scope.row.is_main === 'yes' ? '是' : '否' }}
          </el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="test_type" label="测试类型" width="100" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.is_leaf && scope.row.test_type === 'need_data'" type="warning" size="small">
            依赖型
          </el-tag>
          <el-tag v-else-if="scope.row.is_leaf" type="primary" size="small">
            独立型
          </el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="api_url" label="接口地址" min-width="220" show-overflow-tooltip>
        <template slot-scope="scope">
          <span v-if="scope.row.is_leaf">{{ scope.row.api_url }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="domain" label="域名" width="180" show-overflow-tooltip>
        <template slot-scope="scope">
          <span v-if="scope.row.is_leaf">{{ scope.row.domain }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="api_method" label="请求方式" width="100" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.is_leaf" :type="getMethodType(scope.row.api_method)" size="small">
            {{ scope.row.api_method }}
          </el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="tags" label="标签" width="150">
        <template slot-scope="scope">
          <template v-if="scope.row.is_leaf">
            <el-tag
              v-for="tag in (scope.row.tags || '').split(',').filter(t => t)"
              :key="tag"
              size="mini"
              style="margin-right: 5px">
              {{ tag }}
            </el-tag>
          </template>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="expected_status" label="预期状态" width="100" align="center">
        <template slot-scope="scope">
          <span v-if="scope.row.is_leaf">{{ scope.row.expected_status }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="status" label="状态" width="80" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.is_leaf" :type="scope.row.status === 'enabled' ? 'success' : 'danger'" size="small">
            {{ scope.row.status === 'enabled' ? '启用' : '禁用' }}
          </el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="creator" label="创建人" width="100">
        <template slot-scope="scope">
          <span v-if="scope.row.is_leaf">{{ scope.row.creator }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column prop="create_time" label="创建时间" width="160">
        <template slot-scope="scope">
          <span v-if="scope.row.is_leaf">{{ scope.row.create_time }}</span>
          <span v-else>-</span>
        </template>
      </el-table-column>

      <el-table-column label="操作" width="180" fixed="right">
        <template slot-scope="scope">
          <template v-if="scope.row.is_leaf">
            <el-button size="mini" type="text" @click="handleView(scope.row)">查看</el-button>
            <el-button size="mini" type="text" class="edit-btn" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="text" class="delete-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
          <span v-else>-</span>
        </template>
      </el-table-column>
    </el-table>

    <!-- 统计信息 -->
    <div class="stats-info">
      <el-alert
        :title="`共 ${treeStats.total} 个接口，${treeStats.moduleCount} 个主模块，${treeStats.subModuleCount} 个子模块`"
        type="info"
        :closable="false" />
    </div>

    <!-- 创建/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="900px">
      <el-form ref="form" :model="form" :rules="formRules" label-width="120px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="接口名称" prop="api_name">
              <el-input v-model="form.api_name" placeholder="请输入接口名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="请求方式" prop="api_method">
              <el-select v-model="form.api_method" style="width: 100%">
                <el-option label="GET" value="GET" />
                <el-option label="POST" value="POST" />
                <el-option label="PUT" value="PUT" />
                <el-option label="DELETE" value="DELETE" />
                <el-option label="PATCH" value="PATCH" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="接口类型" prop="api_type">
              <el-select v-model="form.api_type" style="width: 100%">
                <el-option label="登录接口" value="login" />
                <el-option label="普通接口" value="other" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="主模块" prop="module">
              <el-input v-model="form.module" placeholder="请输入主模块名称" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="子模块" prop="sub_module">
              <el-input v-model="form.sub_module" placeholder="请输入子模块名称" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="标签">
              <el-select
                v-model="form.tags"
                multiple
                filterable
                allow-create
                default-first-option
                placeholder="请输入或选择标签"
                style="width: 100%">
                <el-option
                  v-for="item in tagList"
                  :key="item"
                  :label="item"
                  :value="item" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="域名" prop="domain">
              <el-input v-model="form.domain" placeholder="请输入域名，如 http://127.0.0.1:8000" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="接口地址" prop="api_url">
              <el-input v-model="form.api_url" placeholder="请输入接口地址，如 /api/user/info" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="预期状态码" prop="expected_status">
              <el-input-number v-model="form.expected_status" :min="0" :max="999" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="状态" prop="status">
              <el-radio-group v-model="form.status">
                <el-radio label="enabled">启用</el-radio>
                <el-radio label="disabled">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="是否主模块" prop="is_main">
              <el-radio-group v-model="form.is_main">
                <el-radio label="yes">是</el-radio>
                <el-radio label="no">否</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="测试类型" prop="test_type">
              <el-radio-group v-model="form.test_type">
                <el-radio label="independent">独立型</el-radio>
                <el-radio label="need_data">依赖型</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="请求头">
          <el-input
            v-model="form.request_headers"
            type="textarea"
            :rows="3"
            placeholder="JSON格式，如 {&quot;Content-Type&quot;: &quot;application/json&quot;}"
          />
        </el-form-item>
        <el-form-item label="请求参数">
          <el-input
            v-model="form.request_params"
            type="textarea"
            :rows="3"
            placeholder="JSON格式，如 {&quot;name&quot;: &quot;test&quot;}"
          />
        </el-form-item>
        <el-form-item label="请求体">
          <el-input
            v-model="form.request_body"
            type="textarea"
            :rows="3"
            placeholder="JSON格式"
          />
        </el-form-item>
        <el-form-item label="返回参数">
          <el-input
            v-model="form.response_params"
            type="textarea"
            :rows="3"
            placeholder="JSON格式，用于验证返回数据"
          />
        </el-form-item>
        <el-form-item label="接口描述">
          <el-input v-model="form.description" type="textarea" :rows="2" placeholder="请输入接口描述" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>

    <!-- 查看对话框 -->
    <el-dialog title="接口详情" :visible.sync="viewDialogVisible" width="900px">
      <el-descriptions :column="2" border>
        <el-descriptions-item label="接口名称">{{ viewData.api_name }}</el-descriptions-item>
        <el-descriptions-item label="请求方式">
          <el-tag :type="getMethodType(viewData.api_method)">{{ viewData.api_method }}</el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="接口类型">
          <el-tag :type="viewData.api_type === 'login' ? 'success' : 'primary'">
            {{ viewData.api_type === 'login' ? '登录接口' : '普通接口' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="主模块">{{ viewData.module }}</el-descriptions-item>
        <el-descriptions-item label="子模块">{{ viewData.sub_module || '-' }}</el-descriptions-item>
        <el-descriptions-item label="标签">
          <el-tag
            v-for="tag in (viewData.tags || '').split(',').filter(t => t)"
            :key="tag"
            size="mini"
            style="margin-right: 5px">
            {{ tag }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="域名" :span="2">{{ viewData.domain }}</el-descriptions-item>
        <el-descriptions-item label="接口地址" :span="2">{{ viewData.api_url }}</el-descriptions-item>
        <el-descriptions-item label="预期状态码">{{ viewData.expected_status }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="viewData.status === 'enabled' ? 'success' : 'danger'">
            {{ viewData.status === 'enabled' ? '启用' : '禁用' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="是否主模块">
          <el-tag :type="viewData.is_main === 'yes' ? 'success' : 'info'" size="small">
            {{ viewData.is_main === 'yes' ? '是' : '否' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="测试类型">
          <el-tag :type="viewData.test_type === 'need_data' ? 'warning' : 'primary'" size="small">
            {{ viewData.test_type === 'need_data' ? '依赖型' : '独立型' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="创建人">{{ viewData.creator }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ viewData.create_time }}</el-descriptions-item>
        <el-descriptions-item label="更新人">{{ viewData.updater || '-' }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{ viewData.update_time || '-' }}</el-descriptions-item>
        <el-descriptions-item label="请求头" :span="2">
          <pre>{{ formatJson(viewData.request_headers) }}</pre>
        </el-descriptions-item>
        <el-descriptions-item label="请求参数" :span="2">
          <pre>{{ formatJson(viewData.request_params) }}</pre>
        </el-descriptions-item>
        <el-descriptions-item label="请求体" :span="2">
          <pre>{{ formatJson(viewData.request_body) }}</pre>
        </el-descriptions-item>
        <el-descriptions-item label="返回参数" :span="2">
          <pre>{{ formatJson(viewData.response_params) }}</pre>
        </el-descriptions-item>
        <el-descriptions-item label="接口描述" :span="2">{{ viewData.description || '-' }}</el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button @click="viewDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- 导入对话框 -->
    <el-dialog title="批量导入接口" :visible.sync="importDialogVisible" width="600px">
      <div class="import-dialog-content">
        <el-alert
          title="导入说明"
          type="info"
          :closable="false"
          show-icon>
          <template>
            <p>1. 请先导出接口数据查看格式</p>
            <p>2. 支持 JSON 格式导入</p>
            <p>3. 必填字段：api_name（接口名称）、api_url（接口地址）</p>
          </template>
        </el-alert>
        <div class="import-area">
          <el-input
            v-model="importText"
            type="textarea"
            :rows="10"
            placeholder="请输入JSON格式的接口数据"
          />
        </div>
        <div class="import-tip">
          <el-button size="mini" @click="handleExportTemplate">下载模板</el-button>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmImport" :loading="importLoading">确定导入</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getApiList,
  getApiListTree,
  getApiListLazy,
  getApiDetail,
  createApi,
  updateApi,
  deleteApi,
  getModuleList,
  getSubModuleList,
  getTagList,
  exportApi,
  importApi
} from '@/api/test'

export default {
  name: 'ApiManagement',
  data() {
    return {
      // 搜索表单
      searchForm: {
        api_name: '',
        api_type: '',
        module: '',
        sub_module: '',
        tag: '',
        status: '',
        test_type: ''
      },
      // 表格数据（树形）
      tableData: [],
      loading: false,
      // 统计信息
      treeStats: {
        total: 0,
        moduleCount: 0,
        subModuleCount: 0
      },
      // 模块列表
      moduleList: [],
      // 子模块列表
      subModuleList: [],
      // 标签列表
      tagList: [],
      // 导入导出
      importDialogVisible: false,
      importText: '',
      importLoading: false,
      // 对话框
      dialogVisible: false,
      viewDialogVisible: false,
      dialogType: 'create',
      submitLoading: false,
      // 表单
      form: {
        id: 0,
        api_name: '',
        api_url: '',
        domain: 'http://127.0.0.1:8000',
        api_method: 'GET',
        api_type: 'other',
        token: '',
        module: '',
        sub_module: '',
        tags: [],
        request_headers: '',
        request_params: '',
        request_body: '',
        response_params: '',
        expected_status: 200,
        status: 'enabled',
        description: '',
        is_main: 'yes',
        test_type: 'independent'
      },
      // 查看数据
      viewData: {},
      // 表单验证
      formRules: {
        api_name: [{ required: true, message: '请输入接口名称', trigger: 'blur' }],
        api_url: [{ required: true, message: '请输入接口地址', trigger: 'blur' }],
        domain: [{ required: true, message: '请输入域名', trigger: 'blur' }],
        api_method: [{ required: true, message: '请选择请求方式', trigger: 'change' }],
        api_type: [{ required: true, message: '请选择接口类型', trigger: 'change' }],
        status: [{ required: true, message: '请选择状态', trigger: 'change' }],
        is_main: [{ required: true, message: '请选择是否主模块', trigger: 'change' }]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.dialogType === 'create' ? '新增接口' : '编辑接口'
    }
  },
  created() {
    console.log('ApiManagement created, calling fetchData...')
    this.fetchData()
    this.fetchModuleList()
    this.fetchSubModuleList()
    this.fetchTagList()
  },
  methods: {
    // 跳转到测试报告页面
    goToReport() {
      this.$router.push('/dashboard/test/test-report')
    },

    // 获取表格数据（树形）
    async fetchData() {
      this.loading = true
      try {
        const params = {
          api_name: this.searchForm.api_name,
          api_type: this.searchForm.api_type,
          module: this.searchForm.module,
          sub_module: this.searchForm.sub_module,
          tag: this.searchForm.tag,
          status: this.searchForm.status,
          test_type: this.searchForm.test_type
        }
        const res = await getApiListTree(params)
        if (res.code === 200) {
          this.tableData = res.data.tree || []
          // 为子模块节点添加 hasChildren 标记，启用懒加载
          this.markLazyNodes(this.tableData)
          this.treeStats.total = res.data.flat_total || 0
          this.calculateTreeStats(this.tableData)
        }
      } catch (error) {
        this.$message.error(error.message || '获取数据失败')
      } finally {
        this.loading = false
      }
    },

    // 为子模块节点标记 hasChildren，启用懒加载
    markLazyNodes(tree) {
      if (!tree || tree.length === 0) return
      tree.forEach(node => {
        if (!node.is_leaf && node.children && node.children.length > 0) {
          // 子模块节点标记 hasChildren=true，展开时触发 load
          if (node.sub_module) {
            node.hasChildren = true
          }
          // 递归处理子节点
          this.markLazyNodes(node.children)
        }
      })
    },

    // 懒加载子节点
    async loadChildren(row, treeNode, resolve) {
      try {
        const params = {
          module: row.module,
          sub_module: row.sub_module,
          test_type: this.searchForm.test_type
        }
        const res = await getApiListLazy(params)
        if (res.code === 200) {
          // 将接口数据转为叶子节点
          const leafNodes = (res.data || []).map(api => ({
            ...api,
            is_leaf: true
          }))
          resolve(leafNodes)
        } else {
          resolve([])
        }
      } catch (error) {
        console.error('加载子节点失败:', error)
        resolve([])
      }
    },

    // 计算树形统计信息
    calculateTreeStats(tree, stats = { moduleCount: 0, subModuleCount: 0 }) {
      if (!tree || tree.length === 0) return stats

      tree.forEach(node => {
        if (!node.is_leaf) {
          if (node.is_module) {
            stats.moduleCount++
          } else if (node.sub_module) {
            stats.subModuleCount++
          }
          if (node.children && node.children.length > 0) {
            this.calculateTreeStats(node.children, stats)
          }
        }
      })

      this.treeStats = {
        total: this.treeStats.total,
        moduleCount: stats.moduleCount,
        subModuleCount: stats.subModuleCount
      }
    },

    // 获取模块列表
    async fetchModuleList() {
      try {
        const res = await getModuleList()
        if (res.code === 200) {
          this.moduleList = res.data || []
        }
      } catch (error) {
        console.error('获取模块列表失败', error)
      }
    },

    // 获取子模块列表
    async fetchSubModuleList() {
      try {
        const res = await getSubModuleList()
        if (res.code === 200) {
          this.subModuleList = res.data || []
        }
      } catch (error) {
        console.error('获取子模块列表失败', error)
      }
    },

    // 获取标签列表
    async fetchTagList() {
      try {
        const res = await getTagList()
        if (res.code === 200) {
          this.tagList = res.data || []
        }
      } catch (error) {
        console.error('获取标签列表失败', error)
      }
    },

    // 搜索
    handleSearch() {
      this.fetchData()
    },

    // 重置
    handleReset() {
      this.searchForm = {
        api_name: '',
        api_type: '',
        module: '',
        sub_module: '',
        tag: '',
        status: '',
        test_type: ''
      }
      this.fetchData()
    },

    // 获取请求方式标签类型
    getMethodType(method) {
      const types = {
        GET: '',
        POST: 'success',
        PUT: 'warning',
        DELETE: 'danger',
        PATCH: 'info'
      }
      return types[method] || ''
    },

    // 格式化JSON
    formatJson(str) {
      if (!str) return '-'
      try {
        const obj = typeof str === 'string' ? JSON.parse(str) : str
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return str
      }
    },

    // 导出
    async handleExport() {
      try {
        const params = {
          api_type: this.searchForm.api_type,
          module: this.searchForm.module,
          status: this.searchForm.status
        }
        const res = await exportApi(params)
        if (res.code === 200) {
          const data = res.data || []
          const jsonStr = JSON.stringify(data, null, 2)
          const blob = new Blob([jsonStr], { type: 'application/json' })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          const now = new Date()
          const dateStr = now.getFullYear() + '' +
            String(now.getMonth() + 1).padStart(2, '0') +
            String(now.getDate()).padStart(2, '0') +
            String(now.getHours()).padStart(2, '0') +
            String(now.getMinutes()).padStart(2, '0') +
            String(now.getSeconds()).padStart(2, '0')
          link.download = 'api_cases_' + dateStr + '.json'
          link.click()
          URL.revokeObjectURL(url)
          this.$message.success('导出成功')
        }
      } catch (error) {
        this.$message.error(error.message || '导出失败')
      }
    },

    // 导出模板
    handleExportTemplate() {
      const template = [
        {
          api_name: '示例接口',
          api_url: '/api/example',
          domain: 'http://127.0.0.1:8000',
          api_method: 'GET',
          api_type: 'other',
          module: 'admin',
          sub_module: '用户管理',
          tags: '用户,测试',
          request_headers: '{"Content-Type":"application/json"}',
          request_params: '{}',
          request_body: '',
          expected_status: 200,
          status: 'enabled',
          is_main: 'yes',
          description: '接口描述'
        }
      ]
      const jsonStr = JSON.stringify(template, null, 2)
      const blob = new Blob([jsonStr], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'api_template.json'
      link.click()
      URL.revokeObjectURL(url)
    },

    // 显示导入对话框
    showImportDialog() {
      this.importText = ''
      this.importDialogVisible = true
    },

    // 确认导入
    async confirmImport() {
      if (!this.importText.trim()) {
        this.$message.warning('请输入导入数据')
        return
      }
      try {
        const data = JSON.parse(this.importText)
        if (!Array.isArray(data)) {
          this.$message.error('数据格式错误，需要是数组格式')
          return
        }
        this.importLoading = true
        const res = await importApi({ data })
        if (res.code === 200) {
          const result = res.data || {}
          this.$message.success(`导入完成：成功 ${result.success_count} 条，失败 ${result.fail_count} 条`)
          if (result.errors && result.errors.length > 0) {
            console.error('导入错误:', result.errors)
          }
          this.importDialogVisible = false
          this.fetchData()
          this.fetchTagList()
        }
      } catch (error) {
        if (error.message.includes('JSON')) {
          this.$message.error('JSON格式错误')
        } else {
          this.$message.error(error.message || '导入失败')
        }
      } finally {
        this.importLoading = false
      }
    },

    // 显示创建对话框
    showCreateDialog() {
      this.dialogType = 'create'
      this.resetForm()
      this.dialogVisible = true
    },

    // 查看
    async handleView(row) {
      try {
        const res = await getApiDetail({ id: row.id })
        if (res.code === 200) {
          this.viewData = res.data || {}
          this.viewDialogVisible = true
        }
      } catch (error) {
        this.$message.error(error.message || '获取详情失败')
      }
    },

    // 编辑
    handleEdit(row) {
      this.dialogType = 'edit'
      this.getDetail(row.id)
    },

    // 获取详情
    async getDetail(id) {
      try {
        const res = await getApiDetail({ id })
        if (res.code === 200) {
          // 处理tags字段，可能是数组或逗号分隔字符串
          let tags = res.data.tags || ''
          if (Array.isArray(tags)) {
            tags = tags.join(',')
          }
          this.form = {
            ...res.data,
            sub_module: res.data.sub_module || '',
            tags: tags ? tags.split(',') : [],
            request_headers: typeof res.data.request_headers === 'object'
              ? JSON.stringify(res.data.request_headers, null, 2)
              : res.data.request_headers || '',
            request_params: typeof res.data.request_params === 'object'
              ? JSON.stringify(res.data.request_params, null, 2)
              : res.data.request_params || '',
            request_body: typeof res.data.request_body === 'object'
              ? JSON.stringify(res.data.request_body, null, 2)
              : res.data.request_body || '',
            response_params: typeof res.data.response_params === 'object'
              ? JSON.stringify(res.data.response_params, null, 2)
              : res.data.response_params || ''
          }
          this.dialogVisible = true
        }
      } catch (error) {
        this.$message.error(error.message || '获取详情失败')
      }
    },

    // 删除
    handleDelete(row) {
      this.$confirm('确定要删除该接口吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(async () => {
          try {
            const res = await deleteApi({ id: row.id })
            if (res.code === 200) {
              this.$message.success('删除成功')
              this.fetchData()
            }
          } catch (error) {
            this.$message.error(error.message || '删除失败')
          }
        })
        .catch(() => {})
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          this.submitLoading = true
          try {
            // 处理JSON字段
            const data = { ...this.form }
            const jsonFields = ['request_headers', 'request_params', 'request_body', 'response_params']
            jsonFields.forEach(key => {
              if (data[key] && typeof data[key] === 'string') {
                try {
                  JSON.parse(data[key])
                } catch (e) {
                  data[key] = ''
                }
              }
            })
            // 处理tags数组转为逗号分隔字符串
            if (Array.isArray(data.tags)) {
              data.tags = data.tags.join(',')
            }

            const res = this.dialogType === 'create'
              ? await createApi(data)
              : await updateApi(data)

            if (res.code === 200) {
              this.$message.success(this.dialogType === 'create' ? '创建成功' : '更新成功')
              this.dialogVisible = false
              this.fetchData()
              this.fetchModuleList()
              this.fetchSubModuleList()
              this.fetchTagList()
            }
          } catch (error) {
            this.$message.error(error.message || '操作失败')
          } finally {
            this.submitLoading = false
          }
        }
      })
    },

    // 重置表单
    resetForm() {
      this.form = {
        id: 0,
        api_name: '',
        api_url: '',
        domain: 'http://127.0.0.1:8000',
        api_method: 'GET',
        api_type: 'other',
        token: '',
        module: '',
        sub_module: '',
        tags: [],
        request_headers: '',
        request_params: '',
        request_body: '',
        response_params: '',
        expected_status: 200,
        status: 'enabled',
        description: '',
        is_main: 'yes',
        test_type: 'independent'
      }
      this.$refs.form && this.$refs.form.resetFields()
    },

    // 获取行样式类名
    getRowClassName({row}) {
      if (row.is_login) return 'row-login'
      if (!row.is_leaf && !row.sub_module) return 'row-module'
      if (!row.is_leaf) return 'row-sub-module'
      return ''
    }
  }
}
</script>

<style scoped>
.api-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.search-form {
  background: #f5f7fa;
  padding: 20px;
  margin-bottom: 20px;
  border-radius: 4px;
}

.stats-info {
  margin-top: 20px;
}

.edit-btn {
  color: #409eff !important;
}

.delete-btn {
  color: #f56c6c !important;
}

pre {
  margin: 0;
  padding: 10px;
  background: #f5f7fa;
  border-radius: 4px;
  max-height: 200px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.import-dialog-content {
  padding: 10px 0;
}

.import-area {
  margin-top: 20px;
}

.import-tip {
  margin-top: 10px;
  color: #909399;
  font-size: 12px;
}

/* 接口名称单元格 */
.api-name-cell {
  display: flex;
  align-items: center;
}

.api-name-cell.is-login {
  color: #E6A23C;
  font-weight: 500;
}

.api-name-cell.is-module {
  color: #409EFF;
  font-weight: 600;
}

.api-name-cell.is-sub-module {
  color: #606266;
  font-weight: 500;
}

.key-icon {
  margin-right: 6px;
  color: #E6A23C;
}

.folder-icon {
  margin-right: 6px;
  color: #909399;
}

.module-icon {
  margin-right: 6px;
  color: #409EFF;
}

.sub-module-text {
  padding: 2px 8px;
  background: #f0f2f5;
  border-radius: 4px;
  font-size: 12px;
}

.module-text {
  padding: 2px 8px;
  background: #e8f4ff;
  border-radius: 4px;
  font-size: 12px;
  color: #409EFF;
}

/* 树形表格行样式 */
.el-table >>> .row-module {
  background-color: #ecf5ff !important;
}

.el-table >>> .row-sub-module {
  background-color: #fafafa !important;
}

.el-table >>> .row-login {
  background-color: #fffbf0 !important;
}
</style>
